import React, { ReactElement, SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearSession } from '@az/auth-ping';
import { useTranslation } from 'react-i18next';
import { deleteSSOSessions } from '../../../userSSOSessions';

import NavMenu from '../../layout/NavMenu';
import StoreLocation from '../../pages/StoreLocation';
import ConfirmationScreen from '../../layout/ConfirmationScreen';

import { getDriverLogoffStatus, selectPickerStore } from '../../../redux/selectors';
import {
  getCompletedRecalls,
  getCompletedEPOrders,
  getPickToursInProgress,
  getOrders,
} from '../../../redux/actions/orders.actions';
import { setDriverLogoffId } from '../../../redux/actions/driverLogoff.actions';

import { determineDeviceType, getUID } from '../../../utils';

import {
  DESKTOP,
  CT40,
  TC75,
  USER_DATA,
  STORE_RECALL_DATA,
  LAST_LOGIN,
  RECALL_HISTORY_DAYS,
  MOBILE_PRINTER_NUMBER,
  USER_LOGOUT_MSG
} from '../../../constants';

import { AppLogType, NavMenuTab, UserData } from '../../../types';

import styles from './Waffle.css';
import { sendLog } from '../../../redux/actions/log.actions';

const square: ReactElement = <span className={styles.square} />;

const row: ReactElement = (
  <span className={styles.row}>
    {square}
    {square}
    {square}
  </span>
);

type Props = {
  activeNavTab: NavMenuTab;
  storeInputValue: string;
  firstStoreID: string;
  hasOverstockOrder: boolean;
  isOverstockEnabled: boolean;
  setActiveNavTab: (value: SetStateAction<NavMenuTab>) => void;
  setStoreInputValue: (value: SetStateAction<string>) => void;
  setFirstStoreID: (value: SetStateAction<string>) => void;
  createOverstockPlaceholder: () => void;
  clearSelected: () => void;
};

const Waffle = ({
  activeNavTab,
  storeInputValue,
  firstStoreID,
  hasOverstockOrder,
  isOverstockEnabled,
  setActiveNavTab,
  setStoreInputValue,
  setFirstStoreID,
  createOverstockPlaceholder,
  clearSelected,
}: Props): ReactElement => {
  const [isOpened, setIsOpened] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const dispatch = useDispatch();

  const pickerStore = useSelector(selectPickerStore);
  const driverLogoffStatus = useSelector(getDriverLogoffStatus);

  const { t } = useTranslation();
  const translationRoot = 'WAFFLE_MENU';

  useEffect(() => {
    if (driverLogoffStatus !== null) {
      localStorage.removeItem(`${LAST_LOGIN}_${getUID() || ''}`);
      localStorage.removeItem('updateSSOCalled');
      clearSession();
    }
  }, [driverLogoffStatus]);

  const handleOnClick = () => {
    setIsOpened(prevIsOpened => !prevIsOpened);
    setIsAnimating(prevIsAnimating => !prevIsAnimating);
  };

  const driverLogoff = () => {
    localStorage.removeItem(STORE_RECALL_DATA);
    localStorage.removeItem(MOBILE_PRINTER_NUMBER);
    const deviceType = determineDeviceType();
    if (deviceType === DESKTOP) {
      localStorage.removeItem(`${LAST_LOGIN}_${getUID() || ''}`);
      localStorage.removeItem('updateSSOCalled');
      clearSession();
    }
    if (deviceType === CT40 || deviceType === TC75) {
      const userData = sessionStorage.getItem(USER_DATA);
      if (userData) {
        const parsedUserData: UserData = JSON.parse(userData);
        const { sub } = parsedUserData;
        dispatch(setDriverLogoffId(sub));
      }
    }
  };

  const determineTab = () => {
    if (activeNavTab === 'storeLocation') {
      return (
        <StoreLocation
          storeInputValue={storeInputValue}
          firstStoreID={firstStoreID}
          setStoreInputValue={setStoreInputValue}
          setFirstStoreID={setFirstStoreID}
          setActiveNavTab={setActiveNavTab}
        />
      );
    }
    if (activeNavTab === 'generateOverstock') {
      return (
        <ConfirmationScreen
          confirmationText={t(`${translationRoot}.GENERATE_ORDER_CONFIRMATION`)}
          confirmationButtonText={t(`${translationRoot}.GENERATE_ORDER_BUTTON`)}
          handleConfirm={createOverstockPlaceholder}
        />
      );
    }
    return null;
  };

  const isNavTabDisabled =
    !pickerStore.storeID || activeNavTab === 'generateOverstock' || activeNavTab === 'storeLocation';

  return (
    <>
      <button type="button" onClick={handleOnClick} className={styles.button} data-cy="waffle-button">
        <span className={styles.container}>
          {row}
          {row}
          {row}
        </span>
      </button>
      {isOpened || isAnimating ? (
        <NavMenu
          isOpened={isOpened}
          navTabs={[
            {
              id: 'storeLocation',
              text: t(`${translationRoot}.STORE_LOCATION`),
              disabled: activeNavTab === 'storeLocation' || pickerStore.shouldPromptStoreSelection,
              hasArrow: true,
              onClick: () => {
                handleOnClick();
                setActiveNavTab('storeLocation');
              },
            },
            {
              id: 'generateOverstock',
              text: t(`${translationRoot}.GENERATE_OVERSTOCK`),
              disabled:
                !isOverstockEnabled ||
                !pickerStore.storeID ||
                activeNavTab === 'generateOverstock' ||
                activeNavTab === 'storeLocation' ||
                hasOverstockOrder,
              hasArrow: true,
              onClick: () => {
                handleOnClick();
                setActiveNavTab('generateOverstock');
              },
            },
            {
              id: 'recallsHistory',
              text: t(
                `${translationRoot}.${
                  activeNavTab === 'recallsHistory' ? 'RETURN_TO_PENDING_ORDERS' : 'RECALLS_HISTORY'
                }`
              ),
              disabled: isNavTabDisabled,
              onClick: () => {
                clearSelected();
                handleOnClick();
                if (activeNavTab === 'recallsHistory') {
                  setActiveNavTab(null);
                } else {
                  dispatch(
                    getCompletedRecalls({
                      fulfillmentStoreID: pickerStore.storeID || '',
                      daysAgo: RECALL_HISTORY_DAYS,
                      pageNumber: 0,
                    })
                  );
                  setActiveNavTab('recallsHistory');
                }
              },
            },
            {
              id: 'reprintEPLabel',
              text: t(
                `${translationRoot}.${
                  activeNavTab === 'reprintEPLabel' ? 'RETURN_TO_PENDING_ORDERS' : 'REPRINT_EP_LABELS'
                }`
              ),
              disabled: isNavTabDisabled,
              onClick: () => {
                handleOnClick();
                if (activeNavTab === 'reprintEPLabel') {
                  setActiveNavTab(null);
                } else {
                  dispatch(getCompletedEPOrders({ fulfillmentStoreID: pickerStore.storeID || '', pageNumber: 1 }));
                  setActiveNavTab('reprintEPLabel');
                }
              },
            },
            {
              id: 'mobilePrinter',
              text: t(
                `${translationRoot}.${activeNavTab === 'mobilePrinter' ? 'RETURN_TO_PENDING_ORDERS' : 'MOBILE_PRINTER'}`
              ),
              disabled: isNavTabDisabled,
              onClick: () => {
                handleOnClick();
                if (activeNavTab === 'mobilePrinter') {
                  setActiveNavTab(null);
                } else {
                  setActiveNavTab('mobilePrinter');
                }
              },
            },
            {
              id: 'resetPickTour',
              text: t(
                `${translationRoot}.${
                  activeNavTab === 'resetPickTour' ? 'RETURN_TO_PENDING_ORDERS' : 'RESET_PICK_TOUR'
                }`
              ),
              disabled: isNavTabDisabled,
              onClick: () => {
                handleOnClick();
                if (activeNavTab === 'resetPickTour') {
                  dispatch(getOrders({ fulfillmentStoreID: pickerStore.storeID || '', refreshOrders: true }));
                  setActiveNavTab(null);
                } else {
                  dispatch(getPickToursInProgress({ fulfillmentStoreID: pickerStore.storeID || '' }));
                  setActiveNavTab('resetPickTour');
                }
              },
            },
            {
              id: 'logOut',
              text: t(`${translationRoot}.LOGOUT`),
              onClick: async () => {
                const deviceType = determineDeviceType();
                if (deviceType === CT40 || deviceType === TC75) {
                  try {
                    await deleteSSOSessions();
                  } catch (error) {
                    console.log('Error delete SSO sessions', error);
                  }
                }
                handleOnClick();
                driverLogoff();
                dispatch(sendLog({ logEvent: AppLogType.LOG_OUT,logMessage: USER_LOGOUT_MSG }));
              },
            },
          ]}
          handleOnClick={handleOnClick}
          setIsAnimating={setIsAnimating}
        />
      ) : null}
      {determineTab()}
    </>
  );
};

export default Waffle;
